import React, { useState, useEffect, useRef } from 'react';
import { useSharedState } from './SharedContext';
import api from './api';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { toast } from 'react-toastify';
import { Container, Row, Col, Card, Button, ListGroup, Alert, Modal, Form, Badge, Spinner } from 'react-bootstrap';

const BookingPage = ({ user }) => {
    const { selectedDate, setSelectedDate } = useSharedState();
    
    // Helper to create a Date object in local time from a 'YYYY-MM-DD' string
    const getDateFromISOString = (isoDateString) => {
        const [year, month, day] = isoDateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    const [availability, setAvailability] = useState([]);
    const [availableDates, setAvailableDates] = useState(new Set());
    const [error, setError] = useState('');
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [itemToCancel, setItemToCancel] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [customerName, setCustomerName] = useState(user?.fullName || '');
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
    const [listStyle, setListStyle] = useState({});
    const [loadingAvailability, setLoadingAvailability] = useState(true);

    const listContainerRef = useRef(null);

    useEffect(() => {
        setCustomerName(user?.fullName || '');
    }, [user]);
    
    const updateIsDesktop = () => {
        setIsDesktop(window.innerWidth >= 992);
    };

    const fetchAvailableDates = async () => {
        try {
            const response = await api.get(`/api/availability/dates?_=${new Date().getTime()}`);
            setAvailableDates(new Set(response.data));
        } catch (err) {
            toast.error('Failed to fetch available dates.');
        }
    };

    const fetchAvailabilityForDate = async (selectedDateObj) => {
        setLoadingAvailability(true);
        setError('');
        try {
            const dateString = selectedDateObj.toISOString().split('T')[0];
            const response = await api.get(`/api/availability?selected_date=${dateString}&_=${new Date().getTime()}`);
            setAvailability(response.data);
        } catch (err) {
            setError('Failed to fetch availability for the selected date.');
            setAvailability([]);
        } finally {
            setLoadingAvailability(false);
        }
    };

    useEffect(() => {
        window.addEventListener('resize', updateIsDesktop);
        return () => window.removeEventListener('resize', updateIsDesktop);
    }, []);

    useEffect(() => {
        fetchAvailableDates();
        fetchAvailabilityForDate(getDateFromISOString(selectedDate));
    }, [selectedDate]);

    useEffect(() => {
        if (isDesktop && listContainerRef.current) {
            const topOffset = listContainerRef.current.getBoundingClientRect().top;
            const windowHeight = window.innerHeight;
            const maxHeight = windowHeight - topOffset - 30; // 30px buffer
            setListStyle({ maxHeight: `${maxHeight}px`, overflowY: 'auto' });
        } else {
            setListStyle({ maxHeight: 'none', overflowY: 'visible' });
        }
    }, [isDesktop, availability]); // Rerun on resize and when availability data changes

    const handleBookingSuccess = () => {
        setShowBookingModal(false);
        setCustomerName(user?.fullName || '');
        fetchAvailabilityForDate(getDateFromISOString(selectedDate));
        fetchAvailableDates();
    };

    const handleBooking = async () => {
        if (!customerName.trim()) {
            toast.warn('Please enter your name.');
            return;
        }
        try {
            await api.post('/api/bookings', {
                start_time: selectedSlot.start_time,
                customer_name: customerName
            });
            toast.success('Booking confirmed!');
            handleBookingSuccess();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to book slot. It might have been taken.');
        }
    };

    const confirmCancellation = (id) => {
        setItemToCancel(id);
        setShowConfirmModal(true);
    };

    const handleCancelBooking = async () => {
        try {
            await api.delete(`/api/bookings/${itemToCancel}`);
            toast.success('Booking cancelled successfully.');
            handleBookingSuccess();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to cancel booking.');
        } finally {
            setShowConfirmModal(false);
        }
    };

    const openBookingModal = (slot) => {
        setSelectedSlot(slot);
        setShowBookingModal(true);
    };

    const formatTime = (timeStr) => {
        return new Date(timeStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const isTileDisabled = ({ date, view }) => {
        if (view === 'month') {
            const dateString = date.toISOString().split('T')[0];
            return !availableDates.has(dateString);
        }
        return false;
    };

    const calendarColStyle = isDesktop ? { position: 'sticky', top: '76px', alignSelf: 'flex-start' } : {};

    return (
        <Container fluid className="pt-lg-3 px-lg-5 pb-lg-5">
            <h2 className="text-center mb-4">Book an Appointment</h2>
            {error && <Alert variant="danger">{error}</Alert>}
            <Row className="justify-content-center">
                <Col lg={4} md={6} style={calendarColStyle}>
                    <Card className="mb-4">
                        <Card.Body className="d-flex justify-content-center">
                            <Calendar
                                onChange={(newDate) => setSelectedDate(newDate.toISOString().split('T')[0])}
                                value={getDateFromISOString(selectedDate)}
                                minDate={new Date()}
                                tileDisabled={isTileDisabled}
                            />
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={5} md={6}>
                    <Card>
                        <Card.Header as="h5">Available Slots for {getDateFromISOString(selectedDate).toDateString()}</Card.Header>
                        <ListGroup ref={listContainerRef} variant="flush" style={listStyle}>
                            {loadingAvailability ? (
                                <ListGroup.Item className="d-flex justify-content-center">
                                    <Spinner animation="border" />
                                </ListGroup.Item>
                            ) : availability.length > 0 ? availability.map((slot, index) => (
                                <ListGroup.Item key={index} className={`d-flex justify-content-between align-items-center ${slot.booked ? 'bg-light' : ''}`}>
                                    <div>
                                        {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                                        {slot.booked && (
                                            <Badge bg="info" className="ms-2">
                                                Booked by {slot.customer_name}
                                            </Badge>
                                        )}
                                    </div>
                                    {slot.booked ? (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => confirmCancellation(slot.booking_id)}
                                        >
                                            Cancel
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={() => openBookingModal(slot)}
                                        >
                                            Book
                                        </Button>
                                    )}
                                </ListGroup.Item>
                            )) : <ListGroup.Item>No availability for this day.</ListGroup.Item>}
                        </ListGroup>
                    </Card>
                </Col>
            </Row>

            <Modal show={showBookingModal} onHide={() => setShowBookingModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Booking</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You are booking the slot for <strong>{selectedSlot && formatTime(selectedSlot.start_time)}</strong> on <strong>{getDateFromISOString(selectedDate).toDateString()}</strong>.</p>
                    <Form.Group>
                        <Form.Label>Your Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter your name"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBookingModal(false)}>Close</Button>
                    <Button variant="primary" onClick={handleBooking}>Confirm Booking</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Cancellation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to cancel this booking? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Close
                    </Button>
                    <Button variant="danger" onClick={handleCancelBooking}>
                        Cancel Booking
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default BookingPage;